import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Page, PageBody } from '@studio/legacy-components';

import {
  ExperienceDetails,
  WebTriggering,
  UrlTargeting,
  AudiencePanel,
  LaunchpadConfiguration,
} from '@studio/settings-panels';
import {
  ConditionsPropsShape,
  SelectOptionsShape,
  Shape,
} from '@studio/conditions';
import {
  BANNERS_CONDITIONS,
  EVENT_ATTRIBUTE_TRIGGERING,
  EVENT_TRIGGERING,
  LAUNCHPADS_V2,
  MOBILE_FLOWS,
  PIN_MOBILE_CONDITIONS,
  selectFeature,
} from 'next/entities/features';
import {
  Shape as FlowShape,
  selectFlow,
  update as updateFlow,
} from 'next/entities/flows';
import { selectRule, update as updateRule } from 'next/entities/rules';
import { selectUser } from 'next/entities/user';
import { selectAccountUser } from 'next/entities/account-users';
import { replace as replacePage } from 'next/entities/page';
import { drop as dropContentStatus } from 'next/entities/content-status';
import { create as createSegment } from 'next/entities/segments';
import {
  selectCurrentPageUrlOptions,
  selectEventTargetingOptions,
  selectSegmentTargetingOptions,
} from 'next/lib/selectors-options';

import useTitle from 'next/hooks/use-title';
import { selectAccount } from 'next/entities/account';
import { selectInstalled } from 'next/entities/installed';
import { selectConditionsOptions } from 'next/lib/selector-conditions-options';
import {
  isExperienceListByTypeEmpty,
  readExperiences,
} from 'next/entities/experiences';
import { getFlowTargetingSections } from './get-flow-targeting-sections';
import { Loading } from './styled';

export const SettingsPage = ({
  loaded,
  flow,
  rule,
  onLoad,
  onUnload,
  onFlowChange,
  onRuleChange,
  pageUrlOptions,
  domains,
  installedDomains,
  conditionsOptions,
  eventOptions,
  hasEventTriggering,
  hasAttributeTriggering,
  segmentsOptions,
  onSegmentCreate,
  hasMobileFlows,
  hasPinAndMobileConditions,
  hasBannersConditions,
  hasLaunchpad,
  hasNoLaunchpadCreated,
}) => {
  useTitle(`${flow?.name} | Settings | Appcues`);
  useEffect(() => {
    onLoad();
    return () => onUnload();
  }, [onLoad, onUnload]);

  if (!loaded) {
    return <Loading />;
  }

  const sections = getFlowTargetingSections(rule.conditions);

  const handleSectionChange =
    section =>
    (newConditions, ruleChanges = {}) =>
      void onRuleChange({
        ...ruleChanges,
        conditions: {
          and: Object.values({
            ...sections,
            [section]: newConditions,
          }).filter(branch => branch && (branch.and || branch.or).length > 0),
        },
      });

  const handleWidgetFilterChange = widgetFilter => {
    onRuleChange({ widgetFilter });
  };

  return (
    <Page>
      <PageBody>
        <ExperienceDetails
          name={flow.name}
          previewUrl={flow.previewUrl}
          onChange={onFlowChange}
          namePlaceholder="Flow Name"
          nameAriaLabel="Flow Name"
          showPublicName={hasLaunchpad}
        />
        <WebTriggering
          conditions={sections.trigger}
          frequency={rule.frequency}
          eventOptions={eventOptions}
          hasEventTriggering={hasEventTriggering}
          hasAttributeTriggering={hasAttributeTriggering}
          onChange={handleSectionChange('trigger')}
        />
        <UrlTargeting
          domains={domains}
          installedDomains={installedDomains}
          pageUrlOptions={pageUrlOptions}
          urlConditions={sections.url}
          domainConditions={sections.domains}
          onUrlConditionChange={handleSectionChange('url')}
          onDomainConditionChange={handleSectionChange('domains')}
          conditionsOptions={conditionsOptions}
        />
        <AudiencePanel
          conditions={sections.audience}
          conditionsOptions={conditionsOptions}
          experienceName="Flow"
          hasMobileFlows={hasMobileFlows}
          hasPinAndMobileConditions={hasPinAndMobileConditions}
          hasBannersConditions={hasBannersConditions}
          onChange={handleSectionChange('audience')}
          onSegmentCreate={onSegmentCreate}
          segmentsOptions={segmentsOptions}
        />
        <LaunchpadConfiguration
          empty={hasNoLaunchpadCreated}
          widgetFilter={rule.widgetFilter}
          onChange={handleWidgetFilterChange}
        />
      </PageBody>
    </Page>
  );
};

SettingsPage.propTypes = {
  loaded: PropTypes.bool,
  flow: FlowShape,
  rule: Shape,
  onLoad: PropTypes.func,
  onUnload: PropTypes.func,
  onFlowChange: PropTypes.func,
  onRuleChange: PropTypes.func,
  pageUrlOptions: SelectOptionsShape,
  domains: PropTypes.arrayOf(PropTypes.string),
  installedDomains: PropTypes.objectOf(
    PropTypes.shape({
      hostname: PropTypes.string,
    })
  ),
  conditionsOptions: ConditionsPropsShape,
  eventOptions: SelectOptionsShape,
  hasEventTriggering: PropTypes.bool,
  hasAttributeTriggering: PropTypes.bool,
  segmentsOptions: SelectOptionsShape,
  onSegmentCreate: PropTypes.func,
  hasMobileFlows: PropTypes.bool,
  hasPinAndMobileConditions: PropTypes.bool,
  hasBannersConditions: PropTypes.bool,
  hasLaunchpad: PropTypes.bool,
  hasNoLaunchpadCreated: PropTypes.bool,
};

const mapStateToProps = (
  state,
  {
    match: {
      params: { flowId },
    },
  }
) => {
  const flow = selectFlow(state, flowId);
  const rule = selectRule(state, flowId);
  const { id: userId } = selectUser(state);
  const accountUser = selectAccountUser(state, userId);
  const experiences = readExperiences(state);
  const { domains = [] } = selectAccount(state);
  const hasLaunchpad = selectFeature(state, LAUNCHPADS_V2);
  const hasNoLaunchpadCreated =
    hasLaunchpad && isExperienceListByTypeEmpty(state, 'launchpad');

  return {
    segmentsOptions: selectSegmentTargetingOptions(state),
    hasMobileFlows: selectFeature(state, MOBILE_FLOWS),
    hasPinAndMobileConditions: selectFeature(state, PIN_MOBILE_CONDITIONS),
    hasBannersConditions: selectFeature(state, BANNERS_CONDITIONS),
    conditionsOptions: selectConditionsOptions(state),
    installedDomains: selectInstalled(state),
    domains,
    pageUrlOptions: selectCurrentPageUrlOptions(state),
    eventOptions: selectEventTargetingOptions(state),
    hasEventTriggering: selectFeature(state, EVENT_TRIGGERING),
    hasAttributeTriggering: selectFeature(state, EVENT_ATTRIBUTE_TRIGGERING),
    hasLaunchpad,
    hasNoLaunchpadCreated,
    id: flowId,
    loaded: Boolean(
      flow && rule && accountUser && experiences.loading === false
    ),
    flow,
    rule,
  };
};

const mapDispatchToProps = (
  dispatch,
  {
    match: {
      path,
      params: { flowId: id },
    },
  }
) => ({
  onLoad: () => dispatch(replacePage({ path, id })),
  onUnload: () => dispatch(dropContentStatus(id)),
  onFlowChange: delta => dispatch(updateFlow(id, delta)),
  onRuleChange: delta => dispatch(updateRule(id, delta)),
  onSegmentCreate: data => dispatch(createSegment(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SettingsPage);
